import React, { useState } from 'react';
import { useClients } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { useConfig } from '../context/ConfigContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import StyledSelect from './StyledSelect';

export default function AnalysisControls() {
  const { activeClient, updateAnalysisConfig } = useClients();
  const { currentUser } = useUsers();
  const { config } = useConfig();
  const [expanded, setExpanded] = useState(false);

  if (!activeClient) return null;

  const controls = activeClient.analysisConfig?.controls || {};
  const isLocked = activeClient.analysisConfig?.isLocked || false;
  
  const payFrequency = controls.payFrequency || 26;
  const optimizationStrategy = controls.optimizationStrategy || 'employee_benefit';
  const minEmployeeNet = controls.minEmployeeNet || 0;
  const includeFuta = controls.includeFuta || false;
  
  const defaults = config?.engineSettings || {};

  const frequencyLabel = {
    12: 'Monthly',
    24: 'Semi-Monthly',
    26: 'Biweekly',
    52: 'Weekly',
  }[payFrequency] || 'Biweekly';

  const strategyLabel = {
    employee_benefit: 'Employee Benefit',
    employer_savings: 'Employer Savings',
    balanced: 'Balanced',
  }[optimizationStrategy] || 'Employee Benefit';

  const handleUpdate = (updates) => {
    if (isLocked) return;
    updateAnalysisConfig(activeClient.id, {
      controls: { ...controls, ...updates }
    }, currentUser?.id);
  };

  const handleReset = () => {
    if (isLocked) return;
    updateAnalysisConfig(activeClient.id, {
      controls: {
        payFrequency: defaults.payPeriodsPerYear || 26,
        optimizationStrategy: defaults.optimizationStrategy || 'employee_benefit',
        minEmployeeNet: defaults.minEmployeeNetBenefit || 0,
        includeFuta: defaults.includeFutaSavings || false,
      }
    }, currentUser?.id);
  };

  if (!expanded) {
    return (
      <div style={{ 
        padding: '10px 16px', 
        background: isLocked ? C.bg : C.card, 
        borderRadius: 8, 
        border: `1px solid ${isLocked ? C.red : C.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 14,
        opacity: isLocked ? 0.7 : 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: C.textMid }}>
          {isLocked && <Icon name="lock-closed" style={{ fontSize: '14px', color: C.red }} />}
          <Icon name="settings-outline" style={{ fontSize: '16px', color: isLocked ? C.red : C.accent }} />
          <span style={{ color: C.accent, fontWeight: 700 }}>{frequencyLabel}</span>
          <span>|</span>
          <span style={{ color: C.accent, fontWeight: 700 }}>{strategyLabel}</span>
          <span>|</span>
          <span>Min <span style={{ color: C.accent, fontWeight: 700, ...S.mono }}>${minEmployeeNet.toFixed(2)}</span></span>
          <span>|</span>
          <span>FUTA <span style={{ color: C.accent, fontWeight: 700 }}>{includeFuta ? 'On' : 'Off'}</span></span>
          {isLocked && (
            <span style={{ fontSize: 10, color: C.red, background: C.redLight, padding: '2px 6px', borderRadius: 4, fontWeight: 700, marginLeft: 6 }}>
              LOCKED
            </span>
          )}
        </div>
        {!isLocked && (
          <button 
            onClick={() => setExpanded(true)} 
            style={{ 
              padding: '4px 12px', 
              background: 'transparent', 
              border: 'none', 
              color: C.accent, 
              fontSize: 11, 
              fontWeight: 700, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Expand <Icon name="chevron-down-outline" style={{ fontSize: '14px' }} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '16px', 
      background: C.card, 
      borderRadius: 8, 
      border: `1px solid ${isLocked ? C.red : C.border}`,
      marginBottom: 14,
      opacity: isLocked ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isLocked && <Icon name="lock-closed" style={{ fontSize: '16px', color: C.red }} />}
          <Icon name="settings-outline" style={{ fontSize: '18px', color: isLocked ? C.red : C.accent }} />
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Analysis Controls</h4>
          {isLocked && (
            <span style={{ fontSize: 10, color: C.red, background: C.redLight, padding: '3px 8px', borderRadius: 4, fontWeight: 700 }}>
              LOCKED
            </span>
          )}
        </div>
        <button 
          onClick={() => setExpanded(false)} 
          style={{ 
            padding: '4px 12px', 
            background: 'transparent', 
            border: 'none', 
            color: C.accent, 
            fontSize: 11, 
            fontWeight: 700, 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Collapse <Icon name="chevron-up-outline" style={{ fontSize: '14px' }} />
        </button>
      </div>

      {isLocked && (
        <div style={{ 
          marginBottom: 12, 
          padding: '10px 14px', 
          background: C.redLight, 
          border: `1px solid ${C.red}`,
          borderRadius: 6,
          fontSize: 12,
          color: C.red,
          fontWeight: 600,
        }}>
          🔒 Settings are locked. Go to client drawer to unlock.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {/* Pay Frequency */}
        <div>
          <label style={{ ...S.label, marginBottom: 6, fontSize: 11 }}>Pay Frequency</label>
          <StyledSelect
            value={String(payFrequency)}
            onValueChange={(val) => handleUpdate({ payFrequency: +val })}
            disabled={isLocked}
            options={[
              { value: "12", label: "Monthly (12/yr)" },
              { value: "24", label: "Semi-Monthly (24/yr)" },
              { value: "26", label: "Biweekly (26/yr)" },
              { value: "52", label: "Weekly (52/yr)" }
            ]}
            style={{ fontSize: 12 }}
          />
        </div>

        {/* Optimization Strategy */}
        <div>
          <label style={{ ...S.label, marginBottom: 6, fontSize: 11 }}>Optimize For</label>
          <StyledSelect
            value={optimizationStrategy}
            onValueChange={(val) => handleUpdate({ optimizationStrategy: val })}
            disabled={isLocked}
            options={[
              { value: "employee_benefit", label: "Employee Benefit" },
              { value: "employer_savings", label: "Employer Savings" },
              { value: "balanced", label: "Balanced" }
            ]}
            style={{ fontSize: 12 }}
          />
        </div>

        {/* Min Employee Net Benefit */}
        <div>
          <label style={{ ...S.label, marginBottom: 6, fontSize: 11 }}>Min Employee Net (per period)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => handleUpdate({ minEmployeeNet: Math.max(-200, minEmployeeNet - 5) })}
              disabled={isLocked}
              style={{ 
                padding: '7px 10px', 
                background: C.bg, 
                border: `1px solid ${C.border}`, 
                borderRadius: 4, 
                cursor: isLocked ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 700,
                opacity: isLocked ? 0.5 : 1,
              }}
            >
              −
            </button>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.accent, fontWeight: 700, ...S.mono }}>$</span>
              <input
                type="number"
                value={minEmployeeNet}
                onChange={(e) => handleUpdate({ minEmployeeNet: Math.max(-200, Math.min(200, +e.target.value)) })}
                disabled={isLocked}
                step="5"
                min="-200"
                max="200"
                style={{ ...S.input, fontSize: 12, padding: '7px 10px 7px 24px', textAlign: 'right', cursor: isLocked ? 'not-allowed' : 'text' }}
              />
            </div>
            <button
              onClick={() => handleUpdate({ minEmployeeNet: Math.min(200, minEmployeeNet + 5) })}
              disabled={isLocked}
              style={{ 
                padding: '7px 10px', 
                background: C.bg, 
                border: `1px solid ${C.border}`, 
                borderRadius: 4, 
                cursor: isLocked ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 700,
                opacity: isLocked ? 0.5 : 1,
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Include FUTA */}
        <div>
          <label style={{ ...S.label, marginBottom: 6, fontSize: 11 }}>Include FUTA Savings</label>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 10, 
            padding: '7px 12px', 
            background: C.bg, 
            borderRadius: 6, 
            cursor: isLocked ? 'not-allowed' : 'pointer',
            border: `1px solid ${C.borderLight}`,
            opacity: isLocked ? 0.6 : 1,
          }}>
            <input
              type="checkbox"
              checked={includeFuta}
              onChange={(e) => handleUpdate({ includeFuta: e.target.checked })}
              disabled={isLocked}
              style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: includeFuta ? C.accent : C.textMid }}>
              {includeFuta ? 'On' : 'Off'}
            </span>
          </label>
        </div>
      </div>

      <button
        onClick={handleReset}
        disabled={isLocked}
        style={{
          padding: '6px 14px',
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          color: C.textMid,
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked ? 0.5 : 1,
        }}
      >
        Reset to Defaults
      </button>
    </div>
  );
}
